import {createFeatureHub} from '@feature-hub/core';
import {
  createRootLocationTransformer,
  defineHistoryService,
} from '@feature-hub/history-service';
import {loadFederatedModule} from '@feature-hub/module-loader-federation';
import {FeatureHubContextProvider} from '@feature-hub/react';
import {defineAdbContextService} from '@smart/adb-context-service';
import type {EndpointDirectoryV3} from '@smart/endpoint-directory';
import {defineEndpointDirectory} from '@smart/endpoint-directory';
import {defineLocaleService} from '@smart/locale-service';
import type {LocaleV3} from '@smart/locale-service';
import {defineLoginService} from '@smart/login-service';
import {definePageContextService} from '@smart/page-context-service';
import {paymentConfigServiceDefinition} from '@smart/payment-config-service';
import {definePricePreferencesProvider} from '@smart/price-preferences-provider';
import {pubSubServiceDefinition} from '@smart/pub-sub-service';
import {defineSelectedVehicleService} from '@smart/selected-vehicle-service';
import * as React from 'react';
import {createRoot} from 'react-dom/client';

export interface ClientConfig {
  readonly locale: LocaleV3;
  readonly endpointDirectory: EndpointDirectoryV3;
}

export function getClientConfig(): ClientConfig {
  const textContent = document
    .querySelector(`#clientConfig`)!
    .textContent!.trim();

  return JSON.parse(decodeURIComponent(textContent));
}

interface CreateFeatureAppIntegratorConfig {
  App: (config: ClientConfig) => JSX.Element;
  clientConfig?: ClientConfig;
  rootQuerySelector?: string;
  defaultPn18?: string;
  id: string;
}
/**
 * Creates a feature app integrator.
 * @example
 * ```tsx
 * import {createFeatureAppIntegrator, FeatureApp} from '@smart/react-utils';
 * createFeatureAppIntegrator({
 *   App: ({ endpointDirectory: { envName } }) => (
 *      <FeatureApp
 *         name="example"
 *         envName={envName}
 *       />
 *   ),
 *   id: "example-integrator",
 * });
 * ```
 */
export const createFeatureAppIntegrator = ({
  App,
  id,
  defaultPn18 = `HX1ETD3A61EU010418`,
  clientConfig,
  rootQuerySelector = `main`,
}: CreateFeatureAppIntegratorConfig) => {
  const {locale, endpointDirectory} = clientConfig || getClientConfig();

  const historyServiceDefinition = defineHistoryService(
    createRootLocationTransformer({
      consumerPathsQueryParamName: `---`,
      primaryConsumerHistoryKey: id,
    }),
  );

  const localeServiceDefinition = defineLocaleService({
    locale,
    subscribe: () => undefined,
  });

  const pageContextServiceDefinition = definePageContextService({
    initialPageContext: {
      isCustomizerFeatureAppDefined: id.includes(`customizer`),
    },
    subscribe: () => undefined,
  });

  const endpointDirectoryDefinition =
    defineEndpointDirectory(endpointDirectory);

  const selectedVehicleServiceDefinition = defineSelectedVehicleService({
    masterPn18: defaultPn18,
  });

  const loginServiceDefinition = defineLoginService({
    authorizePathname: `/authorize`,
    logoutPathname: `/logout`,
    authorizeStateCookieName: `smart-authorize_state`,
    loginStateCookieName: `smart-login_state`,
  });

  const pricePreferencesProviderDefinition = definePricePreferencesProvider({
    initialPriceType: `private`,
    initialShowPrices: true,
  });

  const adbContextServiceDefinition = defineAdbContextService({
    cookieName: `smart-adb_context_state`,
    resetPathname: `/adb-context-reset`,
  });

  const {featureAppManager} = createFeatureHub(id, {
    moduleLoader: loadFederatedModule,
    featureServiceDefinitions: [
      historyServiceDefinition,
      localeServiceDefinition,
      endpointDirectoryDefinition,
      selectedVehicleServiceDefinition,
      loginServiceDefinition,
      paymentConfigServiceDefinition,
      pageContextServiceDefinition,
      pricePreferencesProviderDefinition,
      pubSubServiceDefinition,
      adbContextServiceDefinition,
    ],
    featureServiceDependencies: {
      [paymentConfigServiceDefinition.id]: `^1.0.0`,
    },
  });

  return createRoot(document.querySelector(rootQuerySelector)!).render(
    <React.StrictMode>
      <FeatureHubContextProvider value={{featureAppManager}}>
        <App locale={locale} endpointDirectory={endpointDirectory} />
      </FeatureHubContextProvider>
    </React.StrictMode>,
  );
};
